import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  BarChart3, FileText, LogOut, User, Lock, Loader2,
  FileQuestion, ClipboardList, Coins, Receipt, Menu, X, UserCog,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const allSidebarItems = [
  { key: "analytics", settingsKey: "nav_analytics", label: "Analytics", icon: BarChart3 },
  { key: "projects", settingsKey: "nav_projects", label: "Projects", icon: FileText },
  { key: "assignments", settingsKey: "nav_assignments", label: "Mini Assignments", icon: ClipboardList },
  { key: "earnings", settingsKey: "nav_earnings", label: "Earnings", icon: Coins },
  { key: "locked", settingsKey: "nav_locked", label: "Invoices Locked", icon: Lock },
  { key: "invoices", settingsKey: "nav_invoices", label: "Invoices", icon: Receipt },
  { key: "profile", settingsKey: null, label: "My Profile", icon: UserCog },
];

const TranslatorLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changing, setChanging] = useState(false);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [portalSections, setPortalSections] = useState({});

  const isMissingProjectPage = location.pathname === "/portal/missing-project";
  const activeSection = isMissingProjectPage ? "missing" : (searchParams.get("view") || "home");

  useEffect(() => {
    axios.get(`${API}/settings`).then(res => {
      if (res.data?.logo_url) setCompanyLogo(res.data.logo_url);
    }).catch(() => {});

    axios.get(`${API}/claims/my-requests`).then(res => {
      const pending = (res.data.requests || []).filter(r => r.status === "pending").length;
      setPendingRequestsCount(pending);
    }).catch(() => {});

    axios.get(`${API}/portal-settings/public`).then(res => {
      setPortalSections(res.data?.settings?.sections || {});
    }).catch(() => {});
  }, []);

  const isOn = (key) => portalSections[key] !== false;
  const sidebarItems = allSidebarItems.filter(item => item.settingsKey === null || isOn(item.settingsKey));
  const showMissingProjects = isOn("nav_missing_projects");

  const handleLogout = () => {
    logout();
    navigate("/portal/login");
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error("Please fill all fields"); return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match"); return;
    }
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters"); return;
    }
    setChanging(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: currentPassword, new_password: newPassword });
      toast.success("Password changed successfully");
      setShowPasswordDialog(false);
      setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally { setChanging(false); }
  };

  const navTo = (key) => {
    if (key === "missing") {
      navigate("/portal/missing-project");
    } else if (key === "home") {
      navigate("/portal");
    } else {
      navigate(`/portal?view=${key}`);
    }
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="translator-layout">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-56 bg-slate-900 text-white flex flex-col transition-transform duration-200 ${mobileMenuOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 md:static md:flex`}>
        {/* Logo */}
        <div className="px-4 py-4 border-b border-slate-700/50">
          <div className="flex items-center gap-2.5">
            {companyLogo ? (
              <img src={companyLogo} alt="Logo" className="w-7 h-7 rounded-lg object-cover" />
            ) : (
              <div className="w-7 h-7 rounded-lg bg-teal-500 flex items-center justify-center">
                <FileText className="w-3.5 h-3.5 text-white" />
              </div>
            )}
            <div>
              <h1 className="text-xs font-bold text-white tracking-wide" style={{ fontFamily: 'Manrope, system-ui' }}>GRAPHIKPRO</h1>
              <p className="text-[8px] text-teal-400 font-medium tracking-wider">TRANSLATOR PORTAL</p>
            </div>
          </div>
        </div>

        {/* User */}
        <div className="px-3 py-3 border-b border-slate-700/50">
          <button
            onClick={() => navTo("home")}
            className={`w-full flex items-center gap-2 px-2 py-2 rounded-lg transition-all ${
              activeSection === "home" ? "bg-teal-500/20 border border-teal-500/30" : "bg-slate-800/60 hover:bg-slate-800"
            }`}
            data-testid="nav-home"
          >
            <div className="w-7 h-7 rounded-full bg-teal-500/20 flex items-center justify-center">
              <User className="w-3.5 h-3.5 text-teal-400" />
            </div>
            <span className={`text-xs font-medium truncate ${activeSection === "home" ? "text-teal-300" : "text-slate-300"}`}>
              {user?.display_name || user?.email}
            </span>
          </button>
        </div>

        {/* Nav Items */}
        <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
          {sidebarItems.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => navTo(key)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                activeSection === key
                  ? "bg-teal-500/20 text-teal-400 border border-teal-500/30"
                  : "text-slate-400 hover:bg-slate-800 hover:text-slate-200"
              }`}
              data-testid={`nav-${key}`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}

          {showMissingProjects && (
          <div className="pt-3 mt-3 border-t border-slate-700/50">
            <button
              onClick={() => navTo("missing")}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                activeSection === "missing"
                  ? "bg-amber-500/20 text-amber-400 border border-amber-500/30"
                  : "text-slate-400 hover:bg-slate-800 hover:text-slate-200"
              }`}
              data-testid="nav-missing-project"
            >
              <FileQuestion className="w-4 h-4" />
              Missing Projects
              {pendingRequestsCount > 0 && (
                <span className="ml-auto px-1.5 py-0.5 rounded-full bg-amber-500 text-white text-[9px] font-bold">
                  {pendingRequestsCount}
                </span>
              )}
            </button>
          </div>
          )}
        </nav>

        {/* Bottom */}
        <div className="px-2 py-3 border-t border-slate-700/50 space-y-1">
          <button
            onClick={() => setShowPasswordDialog(true)}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs text-slate-400 hover:bg-slate-800 hover:text-slate-200 transition-all"
            data-testid="change-password-btn"
          >
            <Lock className="w-4 h-4" />
            Change Password
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs text-slate-400 hover:bg-red-500/10 hover:text-red-400 transition-all"
            data-testid="translator-logout-btn"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 bg-black/50 z-30 md:hidden" onClick={() => setMobileMenuOpen(false)} />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen md:ml-0">
        {/* Mobile Header */}
        <header className="md:hidden sticky top-0 z-20 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
          <button onClick={() => setMobileMenuOpen(true)} className="p-1.5 rounded-lg hover:bg-slate-100">
            <Menu className="w-5 h-5 text-slate-600" />
          </button>
          <span className="text-xs font-bold text-slate-800" style={{ fontFamily: 'Manrope, system-ui' }}>GRAPHIKPRO</span>
          <div className="w-8" />
        </header>

        <main className="flex-1">
          {children}
        </main>
      </div>

      {/* Change Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-slate-400" />
              Change Password
            </DialogTitle>
            <DialogDescription>Update your login password</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label className="text-xs">Current Password</Label>
              <Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="Enter current password" className="h-9" data-testid="current-password-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">New Password</Label>
              <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Enter new password" className="h-9" data-testid="new-password-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Confirm New Password</Label>
              <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm new password" className="h-9" data-testid="confirm-password-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPasswordDialog(false)} size="sm">Cancel</Button>
            <Button onClick={handleChangePassword} disabled={changing} size="sm" className="bg-teal-600 hover:bg-teal-700">
              {changing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              {changing ? "Saving..." : "Save Password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TranslatorLayout;
